import { signInWithCustomToken, type User as FirebaseUser } from "firebase/auth";
import { getFirebaseAuth } from "@/lib/api/firebase-ultra-safe";

const bridgePromises = new Map<string, Promise<FirebaseUser>>();

/**
 * NextAuth(구글) 세션만 있고 Firebase Auth가 없을 때 Custom Token으로 연동합니다.
 * Firestore·Storage·강의실 API는 Firebase Auth가 필요합니다.
 */
export async function ensureFirebaseAuthFromNextAuth(
  uid: string
): Promise<FirebaseUser> {
  const auth = getFirebaseAuth();
  if (!auth) {
    throw new Error("Firebase Auth를 사용할 수 없습니다.");
  }

  if (auth.currentUser?.uid === uid) {
    return auth.currentUser;
  }

  const inFlight = bridgePromises.get(uid);
  if (inFlight) {
    return inFlight;
  }

  const bridgePromise = (async () => {
    const tokenResponse = await fetch("/api/auth/google-session-bridge", {
      method: "POST",
      credentials: "include",
    });

    const tokenPayload = (await tokenResponse.json().catch(() => null)) as {
      success?: boolean;
      customToken?: string;
      uid?: string;
      error?: string;
      details?: string;
    } | null;

    if (
      !tokenResponse.ok ||
      tokenPayload?.success !== true ||
      !tokenPayload?.customToken
    ) {
      throw new Error(
        tokenPayload?.error ||
          tokenPayload?.details ||
          `Google 로그인 연동 실패 (HTTP ${tokenResponse.status})`
      );
    }

    if (tokenPayload.uid && tokenPayload.uid !== uid) {
      throw new Error("로그인 사용자 정보가 일치하지 않습니다.");
    }

    const credential = await signInWithCustomToken(
      auth,
      tokenPayload.customToken
    );

    return credential.user;
  })();

  bridgePromises.set(uid, bridgePromise);

  try {
    return await bridgePromise;
  } finally {
    bridgePromises.delete(uid);
  }
}

export function resolveNextAuthUserId(session: {
  user?: { id?: string; email?: string | null };
}): string | null {
  const id = session.user?.id?.trim();
  if (id) return id;

  const email = session.user?.email;
  if (!email) return null;

  return `google_${email.replace("@", "_").replace(/\./g, "_")}`;
}
